import React, { useRef, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import config from "../../../../../config";
import { useRegionView } from "services/useRegionView";
import { useMaterialUIController } from "context";

const token = config.MAPBOX_ACCESS_TOKEN;

/**
 * selectedPoint: [lat, lon]  (same as your form state)
 * Mapbox:        [lon, lat]
 */
const MapSelect = ({ selectedPoint, setSelectedPoint, isEditing }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(new mapboxgl.Popup({ closeButton: true }));

  const { center, zoom } = useRegionView();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const mapStyle = darkMode
    ? "mapbox://styles/mapbox/dark-v11"
    : "mapbox://styles/mapbox/streets-v12";

  // --- Initialize map once ---
  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: mapStyle,
        center,
        zoom,
        accessToken: token,
      });

      mapRef.current.addControl(new mapboxgl.NavigationControl());
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    map.setStyle(mapStyle);
  }, [darkMode]);

  // --- Toggle cursor based on edit mode ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    map.getCanvas().style.cursor = isEditing ? "pointer" : "default";
  }, [isEditing]);

  // --- Handle clicks ONLY when editable ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const handleClick = (e) => {
      if (!isEditing) return; // ignore clicks when not editing

      const { lng, lat } = e.lngLat; // Mapbox gives [lon, lat]
      // Our state is [lat, lon]
      const point = [lng, lat];

      setSelectedPoint(point);

      popupRef.current
        .setLngLat([lng, lat])
        .setHTML(
          `<h3>Επιλεγμένο Σημείο</h3><p>Longitude: ${lng}<br />Latitude: ${lat}</p>`
        )
        .addTo(map);

      map.flyTo({
        center: [lng, lat],
        zoom: 9,          // less zoomed-in
        essential: true,
      });
    };

    map.on("click", handleClick);
    return () => {
      map.off("click", handleClick);
    };
  }, [isEditing, setSelectedPoint]);

  // --- Change region (from useRegionView) ---
  // Only fly to region center when we DON'T have a selectedPoint.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (selectedPoint && selectedPoint.length === 2) return;

    popupRef.current.remove();

    const fly = () => {
      map.flyTo({
        center,
        zoom,
        essential: true,
      });
    };

    if (map.isStyleLoaded()) {
      fly();
    } else {
      map.once("load", fly);
    }
  }, [center, zoom, selectedPoint]);

  // --- React to external selectedPoint changes (initial load + later) ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    popupRef.current.remove();
    if (!selectedPoint || selectedPoint.length !== 2) return;

    const [lng, lat] = selectedPoint;  // state: [lon, lat]

    const showPopup = () => {
      popupRef.current
        .setLngLat([lng, lat]) // Mapbox: [lon, lat]
        .setHTML(
          `<h3>Επιλεγμένο Σημείο</h3><p>Longitude: ${lng}<br/>Latitude: ${lat}</p>`
        )
        .addTo(map);

      map.flyTo({
        center: [lng, lat],
        zoom: 9,        // less zoomed-in
        essential: true,
      });
    };

    if (map.isStyleLoaded()) {
      showPopup();
    } else {
      map.once("load", showPopup);
    }
  }, [selectedPoint]);

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: "100%",
        height: "100%",
      }}
    />
  );
};

export default MapSelect;
